import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from '@/contexts/Auth0Context'
import { feedSocket } from '@/services/feedSocket'
import { FeedSocketEvent } from '@/types/feed'

interface FeedSocketContextValue {
  isConnected: boolean
  on: <T = unknown>(event: FeedSocketEvent, handler: (payload: T) => void) => () => void
  subscribePost: (postId: string) => void
  unsubscribePost: (postId: string) => void
}

const FeedSocketContext = createContext<FeedSocketContextValue | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useFeedSocket = (): FeedSocketContextValue => {
  const ctx = useContext(FeedSocketContext)
  if (!ctx) throw new Error('useFeedSocket must be used within FeedSocketProvider')
  return ctx
}

interface FeedSocketProviderProps {
  children: ReactNode
}

export const FeedSocketProvider: React.FC<FeedSocketProviderProps> = ({ children }) => {
  const { isAuthenticated, getAccessToken } = useAuth()
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      feedSocket.disconnect()
      setIsConnected(false)
      return
    }

    feedSocket.setTokenGetter(() => getAccessToken())

    let cancelled = false
    feedSocket
      .connect()
      .then(() => {
        if (!cancelled) setIsConnected(true)
      })
      .catch((err) => {
        console.error('Feed socket connection failed:', err)
        if (!cancelled) setIsConnected(false)
      })

    return () => {
      cancelled = true
      feedSocket.disconnect()
      setIsConnected(false)
    }
  }, [isAuthenticated, getAccessToken])

  const value: FeedSocketContextValue = {
    isConnected,
    on: (event, handler) => feedSocket.on(event, handler),
    subscribePost: (postId) => feedSocket.subscribePost(postId),
    unsubscribePost: (postId) => feedSocket.unsubscribePost(postId)
  }

  return <FeedSocketContext.Provider value={value}>{children}</FeedSocketContext.Provider>
}
